import React, { useMemo, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { useFetchOrders } from '@@partner:graphql/query/order/orders.gql'

import { Button, ButtonGroup, Checkbox, Divider, Input, Panel, Pagination, Table } from 'rsuite'
import OrderStatusLabel from '@components/order/common/OrderStatusLabel'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import AppLoader from '@components/common/AppLoader'
import { SORT_ORDER_DESC } from '@@partner:config/graphql'
import { ORDER_STATUS_ACCEPTED_BY_COURIER, ORDER_STATUS_COMPLETED, ORDER_STATUS_DELIVERED, ORDER_STATUS_DEVIVERY_IN_PROGRESS, ORDER_STATUS_PAID, ORDER_STATUS_WAITING_FOR_COURIER, ORDER_STATUS_WAITING_FOR_CUSTOMER } from '@@partner:config/order-status-codes'
import { OrderInfo } from '@@partner:types:app'

const { Column, HeaderCell, Cell } = Table

const GQL_MUTATION_MAKE_PREPAYMENT_CHECK = gql`
  mutation($order_id: ID!, $isPreviewMode: Boolean) {
    utilMakePrepaymentReceipt(order_id: $order_id, preview: $isPreviewMode)
  }
`

const GQL_MUTATION_MAKE_FULLPAYMENT_CHECK = gql`
  mutation($order_id: ID!, $isPreviewMode: Boolean) {
    utilMakeFullpaymentReceipt(order_id: $order_id, preview: $isPreviewMode)
  }
`

type ReceiptType = 'prepayment' | 'fullpayment'

type ReceiptMutationData = {
  utilMakePrepaymentReceipt?: string | null
  utilMakeFullpaymentReceipt?: string | null
}

type ReceiptOrderInfo = OrderInfo & {
  isPrepayable?: boolean
}

const parseOrderIds = (value: string | null) => {
  if (!value) {
    return []
  }

  return Array.from(new Set(
    (value.match(/\d+/g) ?? [])
      .map((item) => parseInt(item, 10))
      .filter((item) => !isNaN(item))
  ))
}

const formatServerResponse = (response?: string | null) => {
  if (!response) {
    return 'Пустой ответ'
  }

  try {
    return JSON.stringify(JSON.parse(response), null, 2)
  }
  catch (_parseError) {
    return response
  }
}

export default function MakeReceiptPage () {
  const [ idFilter, setIdFilter ] = useState<string | null>(null)
  const [ isPreviewMode, setIsPreviewMode ] = useState(true)
  const [ serverResponse, setServerResponse ] = useState('')
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ currentOrderId, setCurrentOrderId ] = useState<number | null>(null)

  const parsedOrderIds = useMemo(() => parseOrderIds(idFilter), [ idFilter ])

  const {
    limit,
    page,
    tableSort,
    handlePageSelect,
    handleSortColumn
  } = useGridHelper()

  const queryLimit = parsedOrderIds.length > 0 ? parsedOrderIds.length : limit

  const {
    data,
    loading,
    error
  } = useFetchOrders({
    page,
    limit: queryLimit,
    filter: {
      statuses: [
        ORDER_STATUS_WAITING_FOR_COURIER,
        ORDER_STATUS_ACCEPTED_BY_COURIER,
        ORDER_STATUS_PAID,
        ORDER_STATUS_DELIVERED,
        ORDER_STATUS_COMPLETED,
        ORDER_STATUS_DEVIVERY_IN_PROGRESS,
        ORDER_STATUS_WAITING_FOR_CUSTOMER
      ],
      ids: parsedOrderIds
    }
  })

  const [ makePrepaymentReceipt, { error: errorPrepayment } ] = useMutation(
    GQL_MUTATION_MAKE_PREPAYMENT_CHECK
  )
  const [ makeFullpaymentReceipt, { error: errorFullpayment } ] = useMutation(
    GQL_MUTATION_MAKE_FULLPAYMENT_CHECK
  )

  if (error) {
    return <div>Error! {JSON.stringify(error)}</div>
  }

  const appendServerResponse = (message: string) => {
    setServerResponse((previousValue) => {
      if (!previousValue) {
        return message
      }

      return `${previousValue}\n\n${message}`
    })
  }

  const runReceiptRequest = async (order: ReceiptOrderInfo, receiptType: ReceiptType) => {
    const mutation = receiptType === 'prepayment'
      ? makePrepaymentReceipt
      : makeFullpaymentReceipt
    const responseKey = receiptType === 'prepayment'
      ? 'utilMakePrepaymentReceipt'
      : 'utilMakeFullpaymentReceipt'
    const receiptLabel = receiptType === 'prepayment'
      ? 'Чек на предоплату'
      : 'Чек на полный расчёт'

    setCurrentOrderId(Number(order.id))

    try {
      const { data } = await mutation({
        variables: {
          isPreviewMode,
          order_id: order.id
        }
      })

      const mutationData = data as ReceiptMutationData | undefined
      appendServerResponse(
        `[${order.id}] ${receiptLabel}\n${formatServerResponse(mutationData?.[responseKey])}`
      )
    }
    catch (requestError) {
      appendServerResponse(
        `[${order.id}] ${receiptLabel}\n${JSON.stringify(requestError, null, 2)}`
      )
    }
  }

  const runReceiptBatch = async (ordersList: ReceiptOrderInfo[], receiptType: ReceiptType) => {
    if (!ordersList.length) {
      return
    }

    setIsSubmitting(true)

    for (const order of ordersList) {
      await runReceiptRequest(order, receiptType)
    }

    setCurrentOrderId(null)
    setIsSubmitting(false)
  }

  const handlePrepaymentReceiptClick = async (order: ReceiptOrderInfo) => {
    setServerResponse('')

    if (window.confirm(`Вы уверены что хотите создать чек на предоплату для ${order.id}`)) {
      await runReceiptBatch([ order ], 'prepayment')
    }
  }

  const handleFullPaymentReceiptClick = async (order: ReceiptOrderInfo) => {
    setServerResponse('')

    if (window.confirm(`Вы уверены что хотите создать чек на полный расчёт для ${order.id}`)) {
      await runReceiptBatch([ order ], 'fullpayment')
    }
  }

  const handleBatchFiscalizationClick = async (receiptType: ReceiptType) => {
    const batchOrders = receiptType === 'prepayment'
      ? orders.filter((order) => order?.isPrepayable)
      : orders
    const receiptLabel = receiptType === 'prepayment'
      ? 'чеки на предоплату'
      : 'чеки на полный расчёт'

    if (!batchOrders.length) {
      return
    }

    setServerResponse('')

    if (window.confirm(`Вы уверены что хотите последовательно создать ${receiptLabel} для ${batchOrders.length} заказов?`)) {
      await runReceiptBatch(batchOrders, receiptType)
    }
  }

  const handleIdFilterChange = (value: string) => {
    setIdFilter(value)
  }

  const orders: ReceiptOrderInfo[] = data?.orders?.edges ?? []
  const pageInfo = data?.orders?.pageInfo
  const prepayableOrders = orders.filter((order) => order?.isPrepayable)

  return (
    <div className="MakeReceipt">
      <Panel header="Логи" bordered collapsible defaultExpanded>
        <h4>Ответ сервера</h4>
        <pre>{serverResponse}</pre>

        {isSubmitting && currentOrderId !== null && (
          <div>Сейчас обрабатывается заказ: {currentOrderId}</div>
        )}

        <h4>Ошибки</h4>
        {errorPrepayment && (
          <pre>
            Ошибка чека предоплаты: {JSON.stringify(errorPrepayment, null, 2)}
          </pre>
        )}
        {errorFullpayment && (
          <pre>
            Ошибка чека оплаты! {JSON.stringify(errorFullpayment, null, 2)}
          </pre>
        )}
      </Panel>
      <Divider />
      <Panel header="Ручное выбитие чеков" shaded collapsible defaultExpanded>
        <div style={{ marginBottom: 12 }}>
          <Checkbox
            checked={isPreviewMode}
            onChange={(_value, checked) => setIsPreviewMode(checked)}
          >
            Режим превью
          </Checkbox>
        </div>

        <Input
          onChange={handleIdFilterChange}
          value={idFilter ?? ''}
          placeholder="Можно вставить массив ID: 123, 456 или [123, 456]"
        />

        <div style={{ display: 'flex', gap: 12, marginTop: 12, marginBottom: 12 }}>
          <Button
            appearance="primary"
            color="orange"
            disabled={loading || isSubmitting || prepayableOrders.length === 0}
            onClick={() => handleBatchFiscalizationClick('prepayment')}
          >
            Чек на предоплату для всех найденных ({prepayableOrders.length})
          </Button>
          <Button
            appearance="primary"
            color="violet"
            disabled={loading || isSubmitting || orders.length === 0}
            onClick={() => handleBatchFiscalizationClick('fullpayment')}
          >
            Чек на полный расчёт для всех найденных ({orders.length})
          </Button>
        </div>

        <Table
          data={orders}
          loading={loading}
          autoHeight
          wordWrap
          sortColumn={tableSort.fieldName}
          sortType={tableSort.direction === SORT_ORDER_DESC ? 'desc' : 'asc'}
          onSortColumn={handleSortColumn}
          renderLoading={() =>
            loading ? <AppLoader.Inner content="Загрузка таблицы" /> : null
          }
        >
          <Column flexGrow={1} verticalAlign="middle" sortable>
            <HeaderCell>ИД</HeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column flexGrow={1} verticalAlign="middle" sortable>
            <HeaderCell>Сумма</HeaderCell>
            <Cell dataKey="finalCost" />
          </Column>
          <Column flexGrow={1} verticalAlign="middle" sortable>
            <HeaderCell>Статус</HeaderCell>
            <Cell dataKey="status">{(rowData) => {
              const row = rowData as ReceiptOrderInfo
              return <OrderStatusLabel status={row.status} />
            }}</Cell>
          </Column>
          <Column flexGrow={1} verticalAlign="middle" sortable>
            <HeaderCell>Клиент</HeaderCell>
            <Cell dataKey="customer_id">{(rowData) => {
              const row = rowData as ReceiptOrderInfo
              return <span>{row?.customer?.fullName}</span>
            }}</Cell>
          </Column>
          <Column flexGrow={1} verticalAlign="middle" sortable>
            <HeaderCell>Телефон</HeaderCell>
            <Cell dataKey="customer_phone" />
          </Column>
          <Column flexGrow={1} verticalAlign="middle" sortable>
            <HeaderCell>Управление</HeaderCell>
            <Cell>{(rowData) => {
              const row = rowData as ReceiptOrderInfo

              return <ButtonGroup>
                {row?.isPrepayable && (
                  <Button
                    color="orange"
                    disabled={isSubmitting}
                    onClick={() => handlePrepaymentReceiptClick(row)}
                  >
                    Чек на предоплату
                  </Button>
                )}
                <Button
                  color="violet"
                  disabled={isSubmitting}
                  onClick={() => handleFullPaymentReceiptClick(row)}
                >
                  Чек на полный расчёт
                </Button>
              </ButtonGroup>
            }}</Cell>
          </Column>
        </Table>

        <Pagination
          last
          first
          next
          prev
          ellipsis
          activePage={page}
          pages={pageInfo?.lastPage}
          total={pageInfo?.lastPage ?? 1}
          maxButtons={10}
          onSelect={handlePageSelect}
        ></Pagination>
      </Panel>
    </div>
  )
}
